import { useContext } from 'react';
// Translation
import { useTranslationContext } from '../../context/TranslationContext';
// Components/ui
import { DialogActions, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// Icons
import { EditCalendar, EventAvailable, CheckCircleOutline } from '@mui/icons-material';
// Context
import { CalendarContext } from '../../context/CalendarContext';

type ActionButtonsProps = {
  isFinalized: boolean;
};
const ActionButtons = ({ isFinalized }: ActionButtonsProps) => {
  const {
    eventDialog: { handleClose },
    handleCreateEvent,
    dialogMode,
    handleStartEditingEvent,
    handleSaveUpdatedEvent,
    handleEndEvent,
    dialogType,
    handleCreateTask,
    handleSaveUpdatedTask,
    selectedTask,
    handleToggleTaskCompleted,
    event,
    task,
    eventCalendarId,
    taskCalendarId,
    isCreatingEvent,
    isUpdatingEvent,
    isCreatingTask,
    isUpdatingTask,
  } = useContext(CalendarContext);
  const t = useTranslationContext();

  // Estados de loading desde el contexto
  const isEvent = dialogType === 'event';
  const isLoadingAddEvent = isCreatingEvent;
  const isLoadingUpdateEvent = isUpdatingEvent;
  const isLoadingEndingEvent = false; // TODO: agregar estado para esta operación
  const isLoadingAddTask = isCreatingTask;
  const isLoadingUpdateTask = isUpdatingTask;

  // Wrapper para las funciones de creación que ahora aceptan parámetros opcionales
  const handleClickCreate = () => {
    if (isEvent) {
      handleCreateEvent();
    } else {
      handleCreateTask();
    }
  };

  const getOnClick = (): (() => void) | undefined => {
    if (dialogMode === 'view') return handleStartEditingEvent;
    if (dialogMode === 'edit') {
      return isEvent ? () => handleSaveUpdatedEvent?.() : () => handleSaveUpdatedTask?.();
    }
    if (dialogMode === 'create') return handleClickCreate;
    return undefined;
  };

  const onClick = getOnClick();

  const ButtonText =
    dialogMode === 'view'
      ? isEvent
        ? t('global.editEvent')
        : t('global.editTask')
      : dialogMode === 'edit'
        ? isEvent
          ? t('global.saveEvent')
          : t('global.saveTask')
        : isEvent
          ? t('global.createEvent')
          : t('global.createTask');

  const loadingMain = isEvent
    ? isLoadingAddEvent || isLoadingUpdateEvent
    : isLoadingAddTask || isLoadingUpdateTask;

  // Validar campos requeridos
  const isFormValid = () => {
    if (dialogMode === 'view') return true; // En modo vista no importa
    
    const form = isEvent ? event : task;
    const calendarId = isEvent ? eventCalendarId : taskCalendarId;
    
    // Validar título (requerido)
    const hasTitle = form?.titleField?.attributes?.value?.trim().length > 0;
    
    // Validar calendario seleccionado (requerido)
    const hasCalendar = Boolean(calendarId);
    
    return hasTitle && hasCalendar;
  };

  const disabledMain = isEvent
    ? isLoadingAddEvent || isLoadingUpdateEvent || isFinalized || !isFormValid()
    : isLoadingAddTask || isLoadingUpdateTask || isFinalized || !isFormValid();

  // Tarea en modo vista: panel simplificado estilo Google Calendar
  const isTaskViewMode = !isEvent && dialogMode === 'view';
  const isTaskCompleted = selectedTask?.isCompleted || false;

  if (isTaskViewMode) {
    // Solo botón "Marcar como completada" (estilo Google Calendar)
    return (
      <DialogActions>
        <LoadingButton
          startIcon={<CheckCircleOutline />}
          size="small"
          loading={isLoadingUpdateTask}
          disabled={isLoadingUpdateTask}
          onClick={handleToggleTaskCompleted}
          variant="contained"
          color={isTaskCompleted ? 'inherit' : 'primary'}
        >
          {isTaskCompleted
            ? t('global.markAsIncomplete', 'Marcar como incompleta')
            : t('global.markAsCompleted', 'Marcar como completada')}
        </LoadingButton>
      </DialogActions>
    );
  }

  // Modo normal (eventos o tareas en edición/creación)
  return (
    <DialogActions>
      <Button variant="outlined" size="small" onClick={handleClose} color="secondary">
        {t('global.cancel')}
      </Button>
      {isEvent && (dialogMode === 'edit' || dialogMode === 'view') && (
        <LoadingButton
          startIcon={<EventAvailable />}
          size="small"
          disabled={isLoadingEndingEvent || isFinalized}
          loading={isLoadingEndingEvent}
          onClick={handleEndEvent}
          variant="contained"
        >
          {t('global.finalizeEvent')}
        </LoadingButton>
      )}
      <LoadingButton
        startIcon={<EditCalendar />}
        size="small"
        loading={loadingMain}
        disabled={disabledMain}
        onClick={onClick}
        variant="contained"
      >
        {ButtonText}
      </LoadingButton>
    </DialogActions>
  );
};

export default ActionButtons;
